import { PUBLIC_HOME } from "@/lib/native";
import SiteMenu from "@/components/SiteMenu";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, MessageCircle, ArrowLeft } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import CelestialBackground from "@/components/CelestialBackground";

export default function SharedAnswer() {
  const { token } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/answer/${token}`).then(({ data }) => setA(data)).catch(() => setError(true));
  }, [token]);

  const cta = user ? "/ask" : "/try";

  return (
    <div className="app-shell relative text-starlight">
      <CelestialBackground />
      <div className="relative z-10 px-5 pb-16" data-testid="shared-answer-page">
        <header className="safe-top box-content h-16 flex items-center gap-2">
          <SiteMenu />
          <Link to={PUBLIC_HOME} className="flex items-center gap-2" data-testid="link-answer-home">
            <ArrowLeft size={16} className="text-[#A7B0C0]" />
            <img src="/astra-mark-128.png" alt="" aria-hidden="true" className="w-9 h-9" />
            <span className="font-display text-lg tracking-[0.3em]">ASTRA</span>
          </Link>
        </header>
        {error && <p data-testid="shared-answer-error" className="text-center text-[#A7B0C0] py-20">This answer couldn't be found.</p>}
        {!a && !error && <Loader2 className="animate-spin gold-text mx-auto my-32" size={28} />}
        {a && (
          <div className="fade-up">
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] gold-text mb-1">
              <MessageCircle size={13} /> Astra Interactive
            </p>
            <h1 className="text-3xl font-light mb-5" data-testid="shared-answer-title">
              {a.name || "A friend"}{a.sun_sign && ` (${a.sun_sign})`} asked the stars
            </h1>
            {a.question && (
              <div className="flex justify-end mb-3">
                <p data-testid="shared-answer-question" className="max-w-[85%] bg-[#D4AF37] text-[#04060B] rounded-2xl rounded-br-md px-4 py-3 text-base leading-relaxed">
                  {a.question}
                </p>
              </div>
            )}
            <div className="max-w-[92%] bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl rounded-bl-md px-4 py-3">
              <p data-testid="shared-answer-teaser" className="text-base leading-relaxed text-[#D6D0C5]">{a.teaser}</p>
              <p className="text-base leading-relaxed text-[#D6D0C5] opacity-40 blur-[2px] select-none" aria-hidden>
                And as the planets shift through the coming days, there is more the stars want you to know about this.
              </p>
              <p className="text-base leading-relaxed text-[#D6D0C5] opacity-15 blur-[4px] select-none" aria-hidden>
                The answer unfolds further when it is written for your own birth chart.
              </p>
            </div>
            <div className="mt-8 bg-[#0F1523] border border-[#D4AF37]/35 rounded-3xl p-6">
              <h2 className="text-2xl font-light mb-2">What would you ask?</h2>
              <p className="text-base text-[#D6D0C5] mb-5">
                Astra Interactive answers your questions using your own birth chart and today's real sky, and remembers
                your conversation for next time.
              </p>
              <Link to={cta} data-testid="btn-shared-answer-cta"
                className="block text-center w-full bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors">
                Try Astra Interactive free
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
